import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Box, Grid, Stack, Typography } from '@mui/material';
import {
  MapCameraChangedEvent,
  MapCameraProps,
} from '@vis.gl/react-google-maps';

import { IcMarkerCfs } from '@/assets/images';
import { FormCard } from '@/components';
import { ExpandControl, FireHydrantMap, IMarker } from '@/components/GoogleMap';
import { QuickNavigationControl } from '@/components/GoogleMap/QuickNavigationControl';
import { defaultMapCenter } from '@/config';
import { ASSIGNMENT_STATUS } from '@/constants';
import { useUnitShiftContext } from '@/hooks';
import { useCFSContext } from '@/hooks/useCFSContext';
import { AssignmentStatus } from '@/models';
import { getAddressInfoCoordinates, getAddressInfoLocation } from '@/services';
import { useUserStore } from '@/store';
import { colors } from '@/theme/variables';
import { formatDistance, openNewWindow } from '@/utils';

import { AreaMapLabels } from './AreaMapLabels';
import { getMapAddressInfo } from './cfsMapAddressInfo';
import { FormItemRoot } from '../styles';

interface CFSMapProps {
  cfsId?: string;
}

export const CFSMap = (props: CFSMapProps) => {
  const { watch } = useFormContext();
  const {
    cfs,
    cfsMapMarkers = [],
    setMapBounds,
    locationBreadCrumb,
    locationMapMarkers,
  } = useCFSContext();
  const { filteredUnitShiftMarkers } = useUnitShiftContext();
  const { account } = useUserStore();
  const [mapCameraValues, setMapCameraValues] = useState<
    Partial<MapCameraProps>
  >(() => {
    const accountCenterCoordinates = account?.settings.center?.coordinates;
    return {
      center: accountCenterCoordinates?.length
        ? {
            lng: accountCenterCoordinates[0],
            lat: accountCenterCoordinates[1],
          }
        : defaultMapCenter,
      zoom: 11,
    };
  });

  const [mapTypeId, setMapTypeId] = useState<string>();
  const formAddressInfo = watch('addressInfo');
  const addressInfo = useMemo(
    () => getMapAddressInfo(formAddressInfo, cfs?.addressInfo),
    [formAddressInfo, cfs?.addressInfo],
  );
  const nearestIntersection = addressInfo?.address?.nearestIntersection;
  const urlParams = useParams();
  const cfsId = props?.cfsId || String(urlParams.cfsId);
  const coordinates = useMemo(
    () => getAddressInfoCoordinates(addressInfo),
    [addressInfo],
  );
  const addressLabel = getAddressInfoLocation(addressInfo);

  const showNearestIntersection =
    account?.settings?.features?.showNearestIntersectionWeb;

  const getMarkerColor = () => {
    if (
      cfs?.isReadyToDispatch &&
      cfs?.assignmentStatus === AssignmentStatus.NEW
    ) {
      return colors.warning.main;
    }

    return cfs
      ? ASSIGNMENT_STATUS[cfs.assignmentStatus].color
      : colors.error.main;
  };
  const markers = useMemo<IMarker[]>(() => {
    if (!coordinates || !cfs) {
      return [];
    }
    return [
      {
        key: 'emergency',
        title: cfs.number,
        position: { lng: coordinates.lng, lat: coordinates.lat },
        icon: <IcMarkerCfs color={getMarkerColor()} />,
        isDroppable: !cfs?.isClosed,
        cfsId,
        cfsNumber: cfs.number,
        address: addressLabel,
      },
    ];
  }, [coordinates, cfs, cfsId, addressLabel]);

  useEffect(() => {
    if (coordinates && cfs) {
      setMapCameraValues({
        center: { lng: coordinates.lng, lat: coordinates.lat },
        zoom: 15,
      });
    }
  }, [coordinates?.lat, coordinates?.lng, cfs?._id]);

  const crossStreetsLabel = useMemo(() => {
    const { crossStreet1, crossStreet2, distance1, distance2 } =
      addressInfo?.address || {};
    if (crossStreet1 && crossStreet2) {
      return `${crossStreet1}(${formatDistance(
        distance1,
      )}) and ${crossStreet2}(${formatDistance(distance2)})`;
    }
    return '-';
  }, [addressInfo?.address]);

  const handleExpandMap = () => {
    let newWindowUrl = `/unit-shifts?cfsId=${cfsId}`;
    if (mapTypeId) {
      newWindowUrl += `&mapTypeId=${mapTypeId}`;
    }
    openNewWindow(newWindowUrl);
  };

  const handleMapBoundsChanged = (e: MapCameraChangedEvent) => {
    setMapBounds(e.detail.bounds);
  };

  return (
    <FormCard classes={{ cardContent: 'map-card-content' }}>
      <FormItemRoot>
        <Stack spacing={2}>
          <FireHydrantMap
            {...mapCameraValues}
            breadcrumb={locationBreadCrumb}
            mapTypeId={mapTypeId}
            markers={markers
              .concat(filteredUnitShiftMarkers || [])
              .concat(cfsMapMarkers)
              .concat(locationMapMarkers || [])}
            showContextMenu={false}
            showStreetViewControl={true}
            onBoundsChanged={handleMapBoundsChanged}
            onHandleMapTypeIdChange={setMapTypeId}
            style={{ height: 342, borderRadius: 8 }}
          >
            <AreaMapLabels addressInfo={addressInfo} />
            <QuickNavigationControl onClick={setMapCameraValues} />
            <ExpandControl onExpand={handleExpandMap} />
          </FireHydrantMap>
          <Box className="map-details-wrapper">
            <Grid container columnSpacing={2}>
              <Grid item xs={6}>
                <Typography
                  sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                >
                  Cross streets
                </Typography>
                <Typography className="map-item-value">
                  {crossStreetsLabel}
                </Typography>
              </Grid>
              {showNearestIntersection && (
                <Grid item xs={6}>
                  <Typography
                    sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                  >
                    Nearest intersection
                  </Typography>
                  <Typography className="map-item-value">
                    {nearestIntersection && nearestIntersection.length > 0
                      ? nearestIntersection.join(' and ')
                      : '-'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Stack>
      </FormItemRoot>
    </FormCard>
  );
};
